import {
  EDITABLE_SCOPES,
  type MeDto,
  QUEUE_FUNCTIONS,
  type QueueFunction,
  queueTaskPermissions,
  type RoleDto,
  type RolePermission,
  SCOPE_LABELS,
  type Scope,
  scopesFor,
  STAFF_FUNCTIONS,
  type StaffFunction,
} from '@bqcrm/shared';
import { Alert, Badge, Button, Checkbox, Grid, Group, Modal, NavLink, Stack, Switch, Table, Tabs, Text, TextInput, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { api, errorText } from '../api';
import { can, useMe, visiblePermissions } from '../auth';
import { ask } from '../confirm';
import { PageHeader } from '../components/PageHeader';
import { QueryState } from '../components/QueryState';
import { notifyError, notifyOk } from '../notify';
import { useSaveShortcut } from '../hooks';
import { SectionCard } from '../components/ui';

const SCOPE_COLORS: Record<Scope, string> = { W: 'indigo', F: 'blue', E: 'cyan', P: 'teal', O: 'gray' };
/** grupy uprawnień do pokazania (bez modułów opcjonalnych wyłączonych w systemie) */
function useVisiblePermissions() {
  const { data: me } = useMe();
  const perms = visiblePermissions(me);
  return { perms, groups: [...new Set(perms.map((p) => p.group))] };
}
const key = (p: RolePermission) => `${p.permission}:${p.scope}`;
const SCOPE_HINTS: Partial<Record<Scope, string>> = {
  W: 'wszystkie rekordy; przy danych firmowych — w firmach pracownika (karta → Dostęp)',
  E: 'zadania i dane ekip, do których pracownik należy',
  P: 'zadania, do których pracownik jest przydzielony',
  O: 'rekordy, których pracownik jest opiekunem lub autorem',
};
const queueName = (q: string) => STAFF_FUNCTIONS[q as StaffFunction] ?? q;

function RoleEditor({ role, editable }: { role: RoleDto; editable: boolean }) {
  const qc = useQueryClient();
  const { perms, groups } = useVisiblePermissions();
  const { data: me } = useMe();
  const [sel, setSel] = useState<Set<string>>(new Set(role.permissions.map(key)));
  const [queues, setQueues] = useState<string[]>(role.queues);
  const [name, setName] = useState(role.name);
  const [onlyGranted, setOnlyGranted] = useState(false);
  useEffect(() => {
    setSel(new Set(role.permissions.map(key)));
    setQueues(role.queues);
    setName(role.name);
  }, [role]);
  const original = useMemo(() => new Set(role.permissions.map(key)), [role]);
  const queuesDirty = [...queues].sort().join() !== [...role.queues].sort().join();
  const dirty = queuesDirty || sel.size !== original.size || [...sel].some((k) => !original.has(k));
  // kolejki do wyboru: magazynier tylko z włączonym modułem magazynu
  const queueOptions = QUEUE_FUNCTIONS.filter((q) => q !== 'magazynier' || me?.features.magazyn);
  const granted = (permission: string) => EDITABLE_SCOPES.some((s) => sel.has(`${permission}:${s}`));
  // kolejka bez uprawnień do jej zadań — zadania nie trafią do nikogo z tą rolą
  const missing = queues.flatMap((q) => queueTaskPermissions(q as QueueFunction).filter((p) => !granted(p)).map((p) => ({ q, p })));
  useSaveShortcut(editable && dirty, () => save.mutate());

  const done = (msg: string) => {
    notifyOk(msg);
    void qc.invalidateQueries({ queryKey: ['roles'] });
  };
  const save = useMutation({
    mutationFn: () =>
      api<RoleDto>(`/roles/${role.id}/permissions`, {
        method: 'PUT',
        body: { permissions: [...sel].map((k) => ({ permission: k.split(':')[0], scope: k.split(':')[1] })), queues },
      }),
    onSuccess: () => done('Zapisano uprawnienia roli — obowiązują od razu'),
    onError: notifyError,
  });
  const rename = useMutation({
    mutationFn: () => api<RoleDto>(`/roles/${role.id}`, { method: 'PUT', body: { name } }),
    onSuccess: () => done('Zmieniono nazwę roli'),
    onError: notifyError,
  });
  const remove = useMutation({
    mutationFn: () => api(`/roles/${role.id}`, { method: 'DELETE' }),
    onSuccess: () => done('Usunięto rolę'),
    onError: notifyError,
  });

  const toggle = (permission: string, scope: Scope, on: boolean) =>
    setSel((s) => {
      const n = new Set(s);
      if (on) n.add(`${permission}:${scope}`);
      else n.delete(`${permission}:${scope}`);
      return n;
    });
  const label = (code: string) => perms.find((p) => p.code === code)?.label ?? code;

  return (
    <Stack>
      <Group align="flex-end" wrap="wrap">
        <TextInput label="Nazwa roli" value={name} disabled={!editable} onChange={(e) => setName(e.currentTarget.value)} w={320} />
        {editable && (
          <Button variant="light" disabled={name.trim() === role.name || name.trim().length < 2} loading={rename.isPending} onClick={() => rename.mutate()}>
            Zmień nazwę
          </Button>
        )}
        <Group gap={6} ml="auto">
          {role.isSystem && <Badge variant="outline" color="gray">wbudowana</Badge>}
          <Badge variant="light" color="gray">
            kont: {role.userCount}
          </Badge>
          {editable && !role.isSystem && (
            <Tooltip label={role.userCount ? 'Najpierw zmień rolę kontom, które ją mają' : 'Usuń rolę'}>
              <Button
                size="xs"
                color="red"
                variant="subtle"
                leftSection={<IconTrash size={14} />}
                disabled={role.userCount > 0}
                onClick={() => void ask(`Rola „${role.name}” zostanie usunięta.`, { title: 'Usunąć rolę?', confirm: 'Usuń', danger: true }).then((ok) => ok && remove.mutate())}
              >
                Usuń
              </Button>
            </Tooltip>
          )}
        </Group>
      </Group>
      <SectionCard title="Zadania w sprawach">
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            Kto ma tę rolę, dostaje zadania z zaznaczonych kolejek — w branżach ustawionych na karcie pracownika (zakładka „Dostęp”).
          </Text>
          <Group gap="lg">
            {queueOptions.map((q) => (
              <Checkbox
                key={q}
                label={STAFF_FUNCTIONS[q]}
                checked={queues.includes(q)}
                disabled={!editable}
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  setQueues((cur) => (on ? [...cur, q] : cur.filter((x) => x !== q)));
                }}
              />
            ))}
          </Group>
          {missing.map(({ q, p }) => (
            <Alert key={`${q}:${p}`} color="orange" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
              Kolejka „{queueName(q)}” wymaga uprawnienia „{label(p)}” — bez niego zadania nie trafią do osób z tą rolą.
            </Alert>
          ))}
        </Stack>
      </SectionCard>
      <Group justify="space-between">
        <Group gap="xs">
          {EDITABLE_SCOPES.map((s) => (
            <Tooltip key={s} label={SCOPE_HINTS[s]} multiline w={260}>
              <Badge color={SCOPE_COLORS[s]} variant="light">
                {s} — {SCOPE_LABELS[s]}
              </Badge>
            </Tooltip>
          ))}
          <Tooltip label="Przy pracowniku w wybranych firmach zakres „wszystkie” obejmuje tylko jego firmy" multiline w={260}>
            <Badge color="teal" variant="outline">
              firmowe = dane firmowe
            </Badge>
          </Tooltip>
        </Group>
        <Switch label="Tylko nadane" checked={onlyGranted} onChange={(e) => setOnlyGranted(e.currentTarget.checked)} />
      </Group>
      <Table.ScrollContainer minWidth={560}>
        <Table withTableBorder verticalSpacing={6}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Czynność</Table.Th>
              {EDITABLE_SCOPES.map((s) => (
                <Table.Th key={s} ta="center" w={64}>
                  <Tooltip label={SCOPE_LABELS[s]}>
                    <Badge color={SCOPE_COLORS[s]} variant="light">
                      {s}
                    </Badge>
                  </Tooltip>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groups.map((g) => {
              const list = perms.filter((p) => p.group === g && (!onlyGranted || granted(p.code)));
              if (!list.length) return null;
              return (
                <Fragment key={g}>
                  <Table.Tr bg="var(--app-subtle)">
                    <Table.Td colSpan={EDITABLE_SCOPES.length + 1}>
                      <Text fw={700} size="sm">
                        {g}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                  {list.map((p) => (
                    <Table.Tr key={p.code}>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm">{p.label}</Text>
                          {p.company && (
                            <Badge size="xs" color="teal" variant="outline">
                              firmowe
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {p.code}
                        </Text>
                      </Table.Td>
                      {EDITABLE_SCOPES.map((s) => {
                        const ok = scopesFor(p.code).includes(s);
                        // zakres bez zastosowania dla tej czynności — bez pola
                        if (!ok && !sel.has(`${p.code}:${s}`)) return <Table.Td key={s} />;
                        return (
                          <Table.Td key={s} ta="center">
                            <Checkbox
                              aria-label={`${p.label}: ${SCOPE_LABELS[s]}`}
                              checked={sel.has(`${p.code}:${s}`)}
                              disabled={!editable || (!ok && !sel.has(`${p.code}:${s}`))}
                              color={SCOPE_COLORS[s]}
                              onChange={(e) => toggle(p.code, s, e.currentTarget.checked)}
                              styles={{ body: { justifyContent: 'center' } }}
                            />
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  ))}
                </Fragment>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {editable && (
        <Group justify="flex-end" pos="sticky" bottom={0} py="sm" style={{ background: 'var(--app-bg)', borderTop: '1px solid var(--app-border)' }}>
          {dirty && (
            <Text size="sm" c="var(--app-text-orange)" fw={500} mr="auto">
              Niezapisane zmiany
            </Text>
          )}
          <Button
            variant="default"
            disabled={!dirty}
            onClick={() => {
              setSel(new Set(original));
              setQueues(role.queues);
            }}
          >
            Przywróć
          </Button>
          <Button disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
            Zapisz uprawnienia
          </Button>
        </Group>
      )}
    </Stack>
  );
}

/** Przegląd: wszystkie role obok siebie (tylko do odczytu). */
function Overview({ roles }: { roles: RoleDto[] }) {
  const { perms, groups } = useVisiblePermissions();
  return (
    <Table.ScrollContainer minWidth={900}>
      <Table withTableBorder withColumnBorders stickyHeader>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Czynność</Table.Th>
            {roles.map((r) => (
              <Table.Th key={r.id} ta="center">
                {r.name}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td>
              <Text size="sm" fw={600}>
                Zadania w sprawach (kolejki)
              </Text>
            </Table.Td>
            {roles.map((r) => (
              <Table.Td key={r.id} ta="center">
                <Text size="xs">{r.queues.map(queueName).join(', ') || '—'}</Text>
              </Table.Td>
            ))}
          </Table.Tr>
          {groups.map((g) => (
            <Fragment key={g}>
              <Table.Tr bg="var(--app-subtle)">
                <Table.Td colSpan={roles.length + 1}>
                  <Text fw={700} size="sm">
                    {g}
                  </Text>
                </Table.Td>
              </Table.Tr>
              {perms.filter((p) => p.group === g).map((p) => (
                <Table.Tr key={p.code}>
                  <Table.Td>
                    <Text size="sm">{p.label}</Text>
                  </Table.Td>
                  {roles.map((r) => {
                    const scopes = r.permissions.filter((x) => x.permission === p.code).map((x) => x.scope);
                    return (
                      <Table.Td key={r.id} ta="center">
                        {scopes.length ? (
                          scopes.map((s) => (
                            <Tooltip key={s} label={SCOPE_LABELS[s]}>
                              <Badge size="sm" color={SCOPE_COLORS[s]} variant="light" mx={1}>
                                {s}
                              </Badge>
                            </Tooltip>
                          ))
                        ) : (
                          <Text c="gray.4">—</Text>
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))}
            </Fragment>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

export function RolesPage({ me }: { me: MeDto }) {
  const q = useQuery({ queryKey: ['roles'], queryFn: () => api<RoleDto[]>('/roles') });
  const qc = useQueryClient();
  const editable = can(me, 'uzytkownicy.zarzadzanie');
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState({ name: '' });
  const current = q.data?.find((r) => r.id === selected) ?? q.data?.[0];
  const create = useMutation({
    mutationFn: () => api<RoleDto>('/roles', { method: 'POST', body: newRole }),
    onSuccess: (r) => {
      notifyOk('Dodano rolę — nadaj jej uprawnienia');
      void qc.invalidateQueries({ queryKey: ['roles'] });
      setSelected(r.id);
      setCreating(false);
      setNewRole({ name: '' });
    },
  });

  return (
    <>
      <PageHeader
        title="Role i uprawnienia"
        description="Rola = uprawnienia (czynność × zakres danych) i kolejki zadań w sprawach. Zmiany obowiązują od razu dla wszystkich kont z daną rolą i są zapisywane w historii operacji."
        actions={
          editable && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              Nowa rola
            </Button>
          )
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        <Tabs defaultValue="edycja" keepMounted={false}>
          <Tabs.List mb="md">
            <Tabs.Tab value="edycja">Edycja roli</Tabs.Tab>
            <Tabs.Tab value="przeglad">Przegląd wszystkich ról</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="edycja">
            <Grid>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <Stack gap={2}>
                  {q.data?.map((r) => (
                    <NavLink
                      key={r.id}
                      active={current?.id === r.id}
                      label={r.name}
                      description={`kont: ${r.userCount}`}
                      onClick={() => setSelected(r.id)}
                    />
                  ))}
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 9 }} style={{ minWidth: 0 }}>
                {current && <RoleEditor key={current.id} role={current} editable={editable} />}
              </Grid.Col>
            </Grid>
          </Tabs.Panel>
          <Tabs.Panel value="przeglad">{q.data && <Overview roles={q.data} />}</Tabs.Panel>
        </Tabs>
      </QueryState>
      <Modal opened={creating} onClose={() => setCreating(false)} title="Nowa rola">
        <Stack>
          <TextInput label="Nazwa" placeholder="np. Kierownik budowy" value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.currentTarget.value })} data-autofocus />
          <Text size="xs" c="dimmed">
            Nowa rola nie ma uprawnień ani kolejek zadań — nadasz je w edytorze, a potem przypiszesz rolę na karcie pracownika („Pracownicy → karta pracownika → Dostęp”).
          </Text>
          {create.error && <Alert color="red">{errorText(create.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreating(false)}>
              Anuluj
            </Button>
            <Button loading={create.isPending} disabled={newRole.name.trim().length < 2} onClick={() => create.mutate()}>
              Dodaj rolę
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
